import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Droplets, Lightbulb, Palette, Fan, ToggleLeft, ToggleRight, Clock, Brain, Play, Pause, Trash2, ChevronDown, ChevronUp, RefreshCw, Sparkles, CalendarClock, AlertTriangle, Check, Power, Plus } from 'lucide-react';
import Card from '../components/ui/Card';
import ApiService from '../services/apiService';
import TranslationService from '../services/translationService';
import ScheduleBuilderModal from '../components/ui/ScheduleBuilderModal';
import DiagnosticsPanel from '../components/DiagnosticsPanel';
import { commitModule } from '../utils/reportMarkers';

// ── Report payload helpers ────────────────────────────────────
// Strips model scratch-pad and stray code fences. Never lets <think> reach the payload.
const stripAiArtifacts = (text) => String(text || '')
  .replace(/<think>[\s\S]*?<\/think>/gi, '')
  .replace(/<think>[\s\S]*$/i, '')
  .replace(/```(?:json|markdown|md)?/gi, '')
  .trim();

const maybeParseJson = (value) => {
  if (typeof value !== 'string') return (value === undefined ? null : value);
  try { return JSON.parse(value); } catch { return value; }
};

const CRON_DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Returns { hour, minute, days:[...] } or null when day-of-month / month are not "*".
const cronToHuman = (expr) => {
  const parts = String(expr || '').trim().split(/\s+/);
  if (parts.length < 5) return null;
  const [minuteRaw, hourRaw, dom, month, dowRaw] = parts;
  if (dom !== '*' || month !== '*') return null;
  const hour = parseInt(hourRaw, 10);
  const minute = parseInt(minuteRaw, 10);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;

  const days = [];
  const pushDay = (n) => {
    const idx = ((Number(n) % 7) + 7) % 7;
    const name = CRON_DAY_NAMES[idx];
    if (name && !days.includes(name)) days.push(name);
  };
  if (dowRaw === '*') {
    CRON_DAY_NAMES.forEach(d => days.push(d));
  } else {
    for (const token of String(dowRaw).split(',')) {
      if (token.includes('-')) {
        const bounds = token.split('-').map(v => parseInt(v, 10));
        if (!Number.isFinite(bounds[0]) || !Number.isFinite(bounds[1])) return null;
        for (let i = bounds[0]; i <= bounds[1]; i++) pushDay(i);
      } else {
        const n = parseInt(token, 10);
        if (!Number.isFinite(n)) return null;
        pushDay(n);
      }
    }
  }
  return { hour, minute, days };
};

// ── Device type icons & colors ───────────────────────────────────────────────
const ACTUATOR_META = {
  Pump:  { icon: Droplets, color: '#3B82F6', activeColor: '#22C55E', label: 'pump' },
  LED:   { icon: Lightbulb, color: '#F59E0B', activeColor: '#FBBF24', label: 'led' },
  RGB:   { icon: Palette, color: '#8B5CF6', activeColor: '#A78BFA', label: 'rgb' },
  Valve: { icon: Droplets, color: '#06B6D4', activeColor: '#22D3EE', label: 'valve' },
  Fan:   { icon: Fan, color: '#64748B', activeColor: '#94A3B8', label: 'fan' },
  Other: { icon: Zap, color: '#C47A2C', activeColor: '#E09B3D', label: 'other' },
};

// ── Animated Toggle Switch ───────────────────────────────────────────────────
const ToggleSwitch = ({ isOn, onToggle, disabled, size = 'lg' }) => {
  const w = size === 'lg' ? 64 : 48;
  const h = size === 'lg' ? 32 : 24;
  const dot = size === 'lg' ? 26 : 18;

  return (
    <motion.button
      onClick={onToggle}
      disabled={disabled}
      className={`relative rounded-full transition-colors duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed
        ${isOn
          ? 'bg-gradient-to-r from-[#6B8F71] to-[#4ADE80] shadow-[0_0_16px_rgba(107,143,113,0.4)]'
          : 'bg-[#1B263B]/15 dark:bg-white/15'
        }`}
      style={{ width: w, height: h, direction: 'ltr' }}
      whileTap={{ scale: 0.95 }}
    >
      <motion.div
        className="absolute top-[3px] rounded-full bg-white shadow-md"
        style={{ width: dot, height: dot, left: 0 }}
        animate={{ x: isOn ? w - dot - 3 : 3 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      />
    </motion.button>
  );
};

// ── RGB Color Picker (compact) ───────────────────────────────────────────────
const RGBPicker = ({ value, onChange }) => {
  const [r, g, b] = [value?.r || 255, value?.g || 200, value?.b || 100];
  const brightness = value?.brightness || 100;

  return (
    <div className="space-y-3 mt-3">
      {[
        { label: 'R', val: r, key: 'r', color: '#EF4444' },
        { label: 'G', val: g, key: 'g', color: '#22C55E' },
        { label: 'B', val: b, key: 'b', color: '#3B82F6' },
      ].map(({ label, val, key, color }) => (
        <div key={key} className="flex items-center gap-3">
          <span className="text-xs font-bold w-4" style={{ color }}>{label}</span>
          <input
            type="range" min="0" max="255" value={val}
            onChange={e => onChange({ ...value, [key]: parseInt(e.target.value) })}
            className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer"
            style={{ accentColor: color }}
          />
          <span className="text-xs font-mono w-8 text-right text-[#1B263B]/60 dark:text-white/50">{val}</span>
        </div>
      ))}
      <div className="flex items-center gap-3">
        <span className="text-xs font-bold w-4 text-[#C47A2C]">☀</span>
        <input
          type="range" min="0" max="100" value={brightness}
          onChange={e => onChange({ ...value, brightness: parseInt(e.target.value) })}
          className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer"
          style={{ accentColor: '#C47A2C' }}
        />
        <span className="text-xs font-mono w-8 text-right text-[#1B263B]/60 dark:text-white/50">{brightness}%</span>
      </div>
      <div className="flex items-center gap-2 mt-1">
        <div className="w-8 h-8 rounded-lg border border-white/20 shadow-inner"
          style={{ backgroundColor: `rgb(${r},${g},${b})`, opacity: brightness / 100 }} />
        <span className="text-[0.65rem] font-mono text-[#1B263B]/40 dark:text-white/30">
          rgb({r},{g},{b}) @ {brightness}%
        </span>
      </div>
    </div>
  );
};

// ── Brightness Slider (for LEDs) ─────────────────────────────────────────────
const BrightnessSlider = ({ value, onChange }) => (
  <div className="flex items-center gap-3 mt-3">
    <Lightbulb size={14} className="text-[#C47A2C]/60" />
    <input
      type="range" min="0" max="100" value={value || 100}
      onChange={e => onChange(parseInt(e.target.value))}
      className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer"
      style={{ accentColor: '#F59E0B' }}
    />
    <span className="text-xs font-mono w-10 text-right text-[#1B263B]/60 dark:text-white/50">{value || 100}%</span>
  </div>
);

// ── Single Actuator Card ─────────────────────────────────────────────────────
const ActuatorCard = ({ actuator, mqttFeedback, onCommand, t }) => {
  const meta = ACTUATOR_META[actuator.device_type] || ACTUATOR_META.Other;
  const Icon = meta.icon;
  const state = actuator.state || {};
  const isOn = state.power === 'on' || state.power === 'ON' ||
    (mqttFeedback && (mqttFeedback === 'ON' || mqttFeedback === '1' || mqttFeedback === 'on'));
  const [localOn, setLocalOn] = useState(isOn);
  const [rgbValue, setRgbValue] = useState(state.r ? { r: state.r, g: state.g, b: state.b, brightness: state.brightness || 100 } : { r: 255, g: 200, b: 100, brightness: 100 });
  const [brightness, setBrightness] = useState(state.brightness || 100);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => { setLocalOn(isOn); }, [isOn]);

  const handleToggle = async () => {
    const newState = !localOn;
    setLocalOn(newState); // Optimistic
    setSending(true);
    try {
      await onCommand(actuator.id, { action: newState ? 'on' : 'off' });
    } catch {
      setLocalOn(!newState); // Revert
    }
    setSending(false);
  };

  const handleRGBApply = async () => {
    setSending(true);
    try {
      await onCommand(actuator.id, { action: 'set_rgb', value: rgbValue });
    } catch { /* toast handled upstream */ }
    setSending(false);
  };

  const handleBrightnessApply = async () => {
    setSending(true);
    try {
      await onCommand(actuator.id, { action: 'set_brightness', value: brightness });
    } catch { /* toast handled upstream */ }
    setSending(false);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
    >
      <Card className={`!p-0 overflow-hidden transition-all duration-300 ${localOn ? 'animate-pulse-halo' : ''}`}>
        {/* Header */}
        <div className={`px-5 py-4 flex items-center justify-between transition-all duration-300
          ${localOn
            ? 'bg-gradient-to-r from-[#6B8F71]/10 to-transparent dark:from-[#6B8F71]/15'
            : ''
          }`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <motion.div
              animate={{ scale: localOn ? [1, 1.15, 1] : 1 }}
              transition={{ duration: 2, repeat: localOn ? Infinity : 0 }}
              className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm transition-colors
                ${localOn
                  ? 'bg-[#6B8F71]/15 dark:bg-[#6B8F71]/25'
                  : 'bg-[#1B263B]/5 dark:bg-white/5'
                }`}
            >
              <Icon size={20} style={{ color: localOn ? meta.activeColor : meta.color }} className={localOn ? 'animate-breathe-glow' : ''} />
            </motion.div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold truncate">{actuator.device_name}</h3>
              <p className="text-[0.65rem] text-[#1B263B]/40 dark:text-white/30 font-medium uppercase tracking-wide">
                {t.control?.[meta.label] || actuator.device_type}
                {actuator.installed_location && ` • ${actuator.installed_location}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Status badge */}
            <span className={`text-[0.6rem] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full
              ${localOn
                ? 'bg-[#6B8F71]/15 text-[#6B8F71]'
                : 'bg-[#1B263B]/5 dark:bg-white/5 text-[#1B263B]/40 dark:text-white/30'
              }`}
            >
              {localOn ? t.control?.pumpOn : t.control?.pumpOff}
            </span>
            <ToggleSwitch isOn={localOn} onToggle={handleToggle} disabled={sending} />
          </div>
        </div>

        {/* Expandable controls (for RGB/LED types) */}
        {(actuator.device_type === 'RGB' || actuator.device_type === 'LED') && (
          <>
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full px-5 py-2 flex items-center justify-center gap-1 text-[0.65rem] font-semibold text-[#C47A2C] hover:bg-[#C47A2C]/5 transition-colors border-t border-[#1B263B]/5 dark:border-white/5"
            >
              {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              {actuator.device_type === 'RGB' ? t.control?.colorPicker : t.control?.brightness}
            </button>
            <AnimatePresence>
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-5 pb-4">
                    {actuator.device_type === 'RGB' ? (
                      <>
                        <RGBPicker value={rgbValue} onChange={setRgbValue} />
                        <motion.button
                          whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                          onClick={handleRGBApply}
                          disabled={sending}
                          className="mt-3 w-full py-2 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#A78BFA] text-white text-xs font-bold shadow-lg disabled:opacity-50"
                        >
                          {t.control?.applySchedule || 'Apply'}
                        </motion.button>
                      </>
                    ) : (
                      <>
                        <BrightnessSlider value={brightness} onChange={setBrightness} />
                        <motion.button
                          whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                          onClick={handleBrightnessApply}
                          disabled={sending}
                          className="mt-3 w-full py-2 rounded-xl bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] text-white text-xs font-bold shadow-lg disabled:opacity-50"
                        >
                          {t.control?.applySchedule || 'Apply'}
                        </motion.button>
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </Card>
    </motion.div>
  );
};

// ── Schedule Timeline Item ───────────────────────────────────────────────────
const ScheduleItem = ({ schedule, onToggle, onDelete, onEdit, t, index }) => {
  const isActive = schedule.is_active;
  const triggerLabel = schedule.trigger_type === 'cron'
    ? `⏰ ${schedule.cron_expression}`
    : schedule.trigger_type === 'threshold'
      ? `📊 ${schedule.threshold_config?.sensor} ${schedule.threshold_config?.operator} ${schedule.threshold_config?.value}`
      : `🤖 ${t.control?.aiSuggested}`;

  const actionPayload = typeof schedule.action_payload === 'string'
    ? JSON.parse(schedule.action_payload)
    : schedule.action_payload || {};

  return (
    <motion.div
      className="animate-timeline-in"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className={`relative flex items-start gap-4 p-4 rounded-xl border transition-all duration-300
        ${isActive
          ? 'border-[#6B8F71]/30 bg-[#6B8F71]/5 dark:bg-[#6B8F71]/10'
          : 'border-[#1B263B]/5 dark:border-white/5 bg-[#1B263B]/[0.02] dark:bg-white/[0.02] opacity-60'
        }`}
      >
        {/* Timeline dot */}
        <div className={`w-3 h-3 rounded-full mt-1 flex-shrink-0 transition-colors
          ${isActive ? 'bg-[#6B8F71] shadow-[0_0_8px_rgba(107,143,113,0.5)]' : 'bg-[#1B263B]/20 dark:bg-white/20'}`} />

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className="text-sm font-bold">{schedule.schedule_name || schedule.actuator_name || 'Schedule'}</h4>
              <p className="text-[0.65rem] text-[#1B263B]/50 dark:text-white/40 mt-0.5 font-mono">{triggerLabel}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <ToggleSwitch isOn={isActive} onToggle={() => onToggle(schedule.id)} size="sm" />
              <motion.button
                whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                onClick={() => onEdit(schedule)}
                className="p-1.5 rounded-lg hover:bg-[#6B8F71]/10 text-[#6B8F71]/60 hover:text-[#6B8F71] transition-all"
              >
                <Clock size={13} />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                onClick={() => onDelete(schedule.id)}
                className="p-1.5 rounded-lg hover:bg-rose-500/10 text-rose-400/60 hover:text-rose-500 transition-all"
              >
                <Trash2 size={13} />
              </motion.button>
            </div>
          </div>

          {/* Action info */}
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="text-[0.6rem] font-semibold px-2 py-0.5 rounded-full bg-[#C47A2C]/10 text-[#C47A2C]">
              {actionPayload.action?.toUpperCase()}
            </span>
            {actionPayload.duration_minutes && (
              <span className="text-[0.6rem] font-semibold px-2 py-0.5 rounded-full bg-[#3B82F6]/10 text-[#3B82F6]">
                {actionPayload.duration_minutes} min
              </span>
            )}
            <span className="text-[0.6rem] font-semibold px-2 py-0.5 rounded-full bg-[#1B263B]/5 dark:bg-white/5 text-[#1B263B]/50 dark:text-white/40">
              {schedule.actuator_type || schedule.device_type || ''}
            </span>
          </div>

          {/* AI Explanation (expandable) */}
          {schedule.ai_explanation && (
            <p className="mt-2 text-[0.65rem] text-[#1B263B]/50 dark:text-white/35 leading-relaxed line-clamp-2">
              {schedule.ai_explanation}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};


// ══════════════════════════════════════════════════════════════════════════════
// MAIN VIEW
// ══════════════════════════════════════════════════════════════════════════════
const ControlAutomationView = ({ userId, t, isRTL, lang, theme, selectedDevice, setReportData }) => {
  const tc = t.control || {};

  // State
  const [actuators, setActuators] = useState([]);
  const [actuatorStates, setActuatorStates] = useState({});
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePanel, setActivePanel] = useState('manual'); // 'manual' | 'automation'

  // AI Suggestion state
  const [suggesting, setSuggesting] = useState(false);
  const [suggestionResult, setSuggestionResult] = useState(null);
  const [suggestionError, setSuggestionError] = useState('');
  const [parsedSchedule, setParsedSchedule] = useState([]);
  const [parsedExplanation, setParsedExplanation] = useState('');
  const [displayExplanation, setDisplayExplanation] = useState('');
  const [applyingAll, setApplyingAll] = useState(false);
  const pollRef = useRef(null);

  // Report payload snapshot (holds ai_suggestions across the static re-writes)
  const controlSnapshotRef = useRef({ generated_at: null, actuators: [], schedules: [], ai_suggestions: [] });
  const lastControlSigRef = useRef('');

  // Manual Builder state
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);

  // ── Data fetching ──────────────────────────────────────────────────────────
  const fetchActuators = useCallback(async () => {
    try {
      const data = await ApiService.get(`/control/actuators?user_id=${userId}`);
      setActuators(Array.isArray(data) ? data : []);
    } catch (e) { console.error('Fetch actuators error:', e); }
  }, [userId]);

  const fetchStates = useCallback(async () => {
    try {
      const data = await ApiService.get(`/control/actuators/states/all?user_id=${userId}`);
      setActuatorStates(data.states || {});
    } catch (e) { console.error('Fetch states error:', e); }
  }, [userId]);

  const fetchSchedules = useCallback(async () => {
    try {
      const data = await ApiService.get(`/control/schedules?user_id=${userId}`);
      setSchedules(Array.isArray(data) ? data : []);
    } catch (e) { console.error('Fetch schedules error:', e); }
  }, [userId]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchActuators(), fetchStates(), fetchSchedules()]);
      setLoading(false);
    };
    if (userId) loadAll();
  }, [userId, fetchActuators, fetchStates, fetchSchedules]);

  // Poll actuator states every 5s
  useEffect(() => {
    if (!userId) return;
    const interval = setInterval(fetchStates, 5000);
    return () => clearInterval(interval);
  }, [userId, fetchStates]);

  // ── Report payload: static control block (works without running the AI) ──────
  useEffect(() => {
    if (!setReportData) return;
    if (!Array.isArray(actuators) || !Array.isArray(schedules)) return;
    if (actuators.length === 0 && schedules.length === 0) return;

    const builtActuators = actuators.slice(0, 20).map(a => {
      const fb = actuatorStates ? actuatorStates[a.id] && actuatorStates[a.id].mqtt_feedback : undefined;
      // Any feedback at all => online. No key at all => null (Unknown). Never false.
      const online = (fb === undefined || fb === null) ? null : true;
      const power = (String(a.state && a.state.power).toLowerCase() === 'on' || ['ON', '1', 'on'].includes(String(fb)))
        ? 'on' : 'off';
      return {
        id: a.id,
        device_name: String(a.device_name || ''),
        device_type: a.device_type || 'Other',
        installed_location: a.installed_location || null,
        power,
        online,
        mqtt_feedback: (fb === undefined || fb === null) ? null : String(fb),
        updated_at: a.updated_at || a.last_updated || null
      };
    });

    const builtSchedules = schedules.slice(0, 20).map(sc => {
      const cronExpr = sc.cron_expression || null;
      return {
        id: sc.id,
        schedule_name: String(sc.schedule_name || sc.actuator_name || 'Schedule'),
        actuator_name: sc.actuator_name ? String(sc.actuator_name) : null,
        actuator_type: sc.actuator_type || sc.device_type || null,
        trigger_type: sc.trigger_type || null,
        cron_expression: cronExpr,
        cron_human: cronExpr ? cronToHuman(cronExpr) : null,
        threshold_config: maybeParseJson(sc.threshold_config),
        action_payload: maybeParseJson(sc.action_payload),
        is_active: Boolean(sc.is_active),
        ai_explanation: sc.ai_explanation || null
      };
    });

    const sig = JSON.stringify([builtActuators, builtSchedules]);
    if (sig === lastControlSigRef.current) return;   // 5s state polling must not thrash global state
    lastControlSigRef.current = sig;

    controlSnapshotRef.current = {
      ...controlSnapshotRef.current,
      generated_at: new Date().toISOString(),
      actuators: builtActuators,
      schedules: builtSchedules
    };

    commitModule(setReportData, 'control',
      { control_automation: { ...controlSnapshotRef.current } }, true, null);
  }, [actuators, actuatorStates, schedules, setReportData]);

  // ── Command handler ────────────────────────────────────────────────────────
  const handleCommand = async (actuatorId, command) => {
    try {
      await ApiService.post(`/control/actuators/${actuatorId}/command`, {
        user_id: userId,
        ...command,
      });
      // Refresh after command
      setTimeout(fetchStates, 500);
      setTimeout(fetchActuators, 1000);
    } catch (e) {
      console.error('Command error:', e);
      throw e;
    }
  };

  // ── Schedule handlers ──────────────────────────────────────────────────────
  const handleToggleSchedule = async (scheduleId) => {
    try {
      await ApiService.post(`/control/schedules/${scheduleId}/toggle?user_id=${userId}`);
      fetchSchedules();
    } catch (e) { console.error('Toggle schedule error:', e); }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await ApiService.delete(`/control/schedules/${scheduleId}?user_id=${userId}`);
      fetchSchedules();
    } catch (e) { console.error('Delete schedule error:', e); }
  };

  // ── AI Suggestion (Streaming) ──────────────────────────────────────────────
  const [streamContent, setStreamContent] = useState('');
  const [thinkingContent, setThinkingContent] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const handleGenerateSchedule = async () => {
    setSuggesting(true);
    setSuggestionResult(null);
    setSuggestionError('');
    setParsedSchedule([]);
    setParsedExplanation('');
    setStreamContent('');
    setThinkingContent('');
    setIsThinking(true);
    setIsComplete(false);

    try {
      window.dispatchEvent(new Event('refresh-credits'));

      const response = await fetch(`${ApiService.BASE_URL}/control/suggest-automation-stream`, {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
          },
          body: JSON.stringify({
              user_id: userId,
              lang: isRTL ? 'ar' : 'en'
          })
      });

      if (!response.ok) throw new Error("Failed to connect to AI engine");

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      
      let isReading = true;
      let inThinkBlock = false;
      let fullContent = '';
      
      while (isReading) {
          const { done, value } = await reader.read();
          if (done) {
              isReading = false;
              break;
          }
          
          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');
          
          for (const line of lines) {
              if (line.startsWith('data: ')) {
                  const dataStr = line.substring(6);
                  if (dataStr.trim() === '[DONE]') {
                      isReading = false;
                      break;
                  }
                  
                  try {
                      const dataObj = JSON.parse(dataStr);
                      if (dataObj.error) {
                         setSuggestionError(dataObj.error.message);
                         continue;
                      }

                      const delta = dataObj.choices?.[0]?.delta;
                      
                      if (delta?.reasoning) {
                          setThinkingContent(prev => prev + delta.reasoning);
                      } 
                      else if (delta?.content) {
                          let contentText = delta.content;
                          
                          if (contentText.includes('<think>')) {
                              inThinkBlock = true;
                              const parts = contentText.split('<think>');
                              if (parts[0]) { setStreamContent(prev => prev + parts[0]); fullContent += parts[0]; }
                              if (parts[1]) setThinkingContent(prev => prev + parts[1]);
                          } else if (contentText.includes('</think>')) {
                              inThinkBlock = false;
                              setIsThinking(false);
                              const parts = contentText.split('</think>');
                              if (parts[0]) setThinkingContent(prev => prev + parts[0]);
                              if (parts[1]) { setStreamContent(prev => prev + parts[1]); fullContent += parts[1]; }
                          } else {
                              if (inThinkBlock) {
                                  setThinkingContent(prev => prev + contentText);
                              } else {
                                  if (isThinking) setIsThinking(false);
                                  setStreamContent(prev => prev + contentText);
                                  fullContent += contentText;
                              }
                          }
                      }
                  } catch (e) {
                      console.error("Stream parse error:", e, dataStr);
                  }
              }
          }
      }
      
      setIsComplete(true);
      setSuggesting(false);
      setIsThinking(false);

      // Try to parse the JSON response
      let aiSuggestions = [];
      let explanationText = '';
      try {
        const jsonMatch = fullContent.match(/```json\s*([\s\S]*?)```/) ||
          fullContent.match(/\{[\s\S]*"machine_schedule"[\s\S]*\}/);
        let parsed;
        if (jsonMatch) {
          parsed = JSON.parse(jsonMatch[1] || jsonMatch[0]);
        } else {
          parsed = JSON.parse(fullContent);
        }
        setParsedSchedule(parsed.machine_schedule || []);
        setParsedExplanation(parsed.explanation || '');
        aiSuggestions = Array.isArray(parsed.machine_schedule) ? parsed.machine_schedule : [];
        explanationText = parsed.explanation || '';
      } catch {
        setParsedExplanation(fullContent);
        explanationText = fullContent;
      }

      // Report payload: AI suggestions + explanation, written from the LOCAL fullContent
      // accumulator and BEFORE handleApplyAllSchedules can clear the parsed state.
      if (setReportData) {
        controlSnapshotRef.current = {
          ...controlSnapshotRef.current,
          generated_at: new Date().toISOString(),
          ai_suggestions: aiSuggestions.slice(0, 10)
        };
        commitModule(setReportData, 'control', {
          control_automation: { ...controlSnapshotRef.current },
          control_ai_text: stripAiArtifacts(explanationText)
        }, true, null);
      }

    } catch (e) {
      setSuggesting(false);
      setIsThinking(false);
      setSuggestionError(e.message || 'Failed to start suggestion');
    }
  };

  useEffect(() => {
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, []);

  // ── Dynamic AI text translation on language change ─────────────────────────
  useEffect(() => {
    if (!parsedExplanation) { setDisplayExplanation(''); return; }
    let cancelled = false;
    const translateExplanation = async () => {
      const targetLang = lang === 'en' ? 'en' : 'ar';
      try {
        const translated = await TranslationService.translateText(parsedExplanation, targetLang);
        if (!cancelled) setDisplayExplanation(translated);
      } catch {
        if (!cancelled) setDisplayExplanation(parsedExplanation);
      }
    };
    translateExplanation();
    return () => { cancelled = true; };
  }, [parsedExplanation, lang]);

  const handleApplyAllSchedules = async () => {
    if (!parsedSchedule.length) return;
    setApplyingAll(true);
    try {
      for (const rule of parsedSchedule) {
        await ApiService.post('/control/schedules', {
          user_id: userId,
          actuator_id: rule.actuator_id,
          schedule_name: rule.schedule_name || 'AI Schedule',
          trigger_type: rule.trigger_type || 'cron',
          cron_expression: rule.cron_expression || null,
          threshold_config: rule.threshold_config || null,
          action_payload: rule.action_payload,
          is_active: true,
          ai_explanation: parsedExplanation?.substring(0, 500) || null,
        });
      }
      await fetchSchedules();
      setParsedSchedule([]);
      setParsedExplanation('');
      setSuggestionResult(null);
    } catch (e) {
      console.error('Apply schedule error:', e);
    }
    setApplyingAll(false);
  };

  const handleSaveSchedule = async (payload, id) => {
    try {
      if (id) {
        await ApiService.put(`/control/schedules/${id}`, { ...payload, user_id: userId });
      } else {
        await ApiService.post(`/control/schedules`, { ...payload, user_id: userId });
      }
      setIsBuilderOpen(false);
      setEditingSchedule(null);
      fetchSchedules();
    } catch (e) {
      console.error('Save schedule error:', e);
      setSuggestionError(e.message || 'Failed to save schedule');
    }
  };

  const openEditModal = (schedule) => {
    setEditingSchedule(schedule);
    setIsBuilderOpen(true);
  };

  // ── Loading State ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <span className="w-8 h-8 border-2 border-[#C47A2C]/40 border-t-[#C47A2C] rounded-full animate-spin" />
          <span className="text-[#1B263B]/40 dark:text-white/40 text-sm">{tc.title}...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight flex items-center gap-3">
            <Zap size={24} className="text-[#C47A2C]" />
            {tc.title}
          </h1>
          <p className="text-sm text-[#1B263B]/50 dark:text-white/40 mt-1">{tc.smartScheduleDesc}</p>
        </div>

        {/* Panel switcher */}
        <div className="flex gap-1 bg-white/60 dark:bg-[#1B263B]/60 backdrop-blur-md p-1 rounded-xl border border-[#1B263B]/10 dark:border-white/10 shadow-sm">
          <button
            onClick={() => setActivePanel('manual')}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2
              ${activePanel === 'manual'
                ? 'bg-[#C47A2C]/10 text-[#C47A2C] border-b-2 border-[#C47A2C]'
                : 'text-[#1B263B]/50 dark:text-white/40 hover:text-[#C47A2C]'
              }`}
          >
            <Power size={15} /> {tc.manualControl}
          </button>
          <button
            onClick={() => setActivePanel('automation')}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2
              ${activePanel === 'automation'
                ? 'bg-[#C47A2C]/10 text-[#C47A2C] border-b-2 border-[#C47A2C]'
                : 'text-[#1B263B]/50 dark:text-white/40 hover:text-[#C47A2C]'
              }`}
          >
            <Brain size={15} /> {tc.aiAutomation}
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* MANUAL CONTROL PANEL                                                  */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence mode="wait">
        {activePanel === 'manual' && (
          <motion.div
            key="manual"
            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
          >
            {actuators.length === 0 ? (
              <Card className="!p-12 text-center">
                <Zap size={48} className="mx-auto text-[#1B263B]/15 dark:text-white/10 mb-4" />
                <p className="text-sm text-[#1B263B]/50 dark:text-white/40">{tc.noActuators}</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {actuators.map(a => (
                  <ActuatorCard
                    key={a.id}
                    actuator={a}
                    mqttFeedback={actuatorStates[a.id]?.mqtt_feedback}
                    onCommand={handleCommand}
                    t={t}
                  />
                ))}
              </div>
            )}

            {/* Quick refresh */}
            <div className="flex justify-center mt-4">
              <motion.button
                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                onClick={() => { fetchActuators(); fetchStates(); }}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold text-[#1B263B]/40 dark:text-white/30 hover:text-[#C47A2C] hover:bg-[#C47A2C]/5 transition-all"
              >
                <RefreshCw size={13} /> {t.dashboard?.refresh || 'Refresh'}
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* AI AUTOMATION PANEL                                               */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {activePanel === 'automation' && (
          <motion.div
            key="automation"
            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            {/* Generate AI Schedule */}
            <Card className="!p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#C47A2C]/20 to-[#8B5CF6]/20 flex items-center justify-center">
                    <Sparkles size={22} className="text-[#C47A2C]" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold">{tc.generateSchedule}</h3>
                    <p className="text-[0.65rem] text-[#1B263B]/40 dark:text-white/30 mt-0.5">{tc.smartScheduleDesc}</p>
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.04, y: -1 }} whileTap={{ scale: 0.97 }}
                  onClick={handleGenerateSchedule}
                  disabled={suggesting || actuators.length === 0}
                  className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#C47A2C] to-[#A36322] text-white font-bold text-sm shadow-[0_4px_16px_rgba(196,122,44,0.3)] hover:shadow-[0_6px_24px_rgba(196,122,44,0.4)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {suggesting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      {tc.generating}
                    </>
                  ) : (
                    <>
                      <Brain size={16} />
                      {tc.generateSchedule}
                    </>
                  )}
                </motion.button>
              </div>

              {/* Error */}
              {suggestionError && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center gap-2"
                >
                  <AlertTriangle size={16} className="text-rose-500" />
                  <span className="text-xs text-rose-600 dark:text-rose-400">{suggestionError}</span>
                </motion.div>
              )}
            </Card>

            {/* Diagnostics Panel */}
            {(streamContent || thinkingContent) && (
              <div className="mt-4">
                <DiagnosticsPanel
                  isStreaming={suggesting}
                  streamContent={streamContent}
                  thinkingContent={thinkingContent}
                  isThinking={isThinking}
                  isComplete={isComplete}
                  hasError={!!suggestionError}
                  errorMessage={suggestionError}
                  onRetry={handleGenerateSchedule}
                  onNewAnalysis={() => { setStreamContent(''); setThinkingContent(''); setIsComplete(false); }}
                  t={tc}
                  isRTL={isRTL}
                />
              </div>
            )}

            {/* AI Suggestion Result */}
            <AnimatePresence>
              {(parsedSchedule.length > 0) && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="space-y-4"
                >
                  {/* Suggested Schedule Timeline */}
                  {parsedSchedule.length > 0 && (
                    <Card className="!p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <CalendarClock size={16} className="text-[#C47A2C]" />
                          <h3 className="text-sm font-bold">{tc.applySchedule}</h3>
                          <span className="text-[0.6rem] font-bold px-2 py-0.5 rounded-full bg-[#C47A2C]/10 text-[#C47A2C]">
                            {parsedSchedule.length} rules
                          </span>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
                          onClick={handleApplyAllSchedules}
                          disabled={applyingAll}
                          className="px-4 py-2 rounded-xl bg-[#6B8F71] hover:bg-[#5A7D60] text-white text-xs font-bold shadow-lg flex items-center gap-2 disabled:opacity-50"
                        >
                          {applyingAll ? (
                            <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                          ) : (
                            <Check size={14} />
                          )}
                          {tc.applyAll}
                        </motion.button>
                      </div>
                      <div className="space-y-3">
                        {parsedSchedule.map((rule, i) => (
                          <div
                            key={i}
                            className="animate-timeline-in flex items-center gap-3 p-3 rounded-xl border border-[#1B263B]/5 dark:border-white/5 bg-[#1B263B]/[0.02] dark:bg-white/[0.02]"
                            style={{ animationDelay: `${i * 0.1}s` }}
                          >
                            <div className="w-2 h-2 rounded-full bg-[#C47A2C] flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="text-xs font-bold">{rule.schedule_name}</span>
                              <div className="flex flex-wrap gap-1.5 mt-1">
                                <span className="text-[0.55rem] font-mono px-1.5 py-0.5 rounded bg-[#1B263B]/5 dark:bg-white/5">
                                  {rule.trigger_type === 'cron' ? `⏰ ${rule.cron_expression}` : `📊 threshold`}
                                </span>
                                <span className="text-[0.55rem] font-mono px-1.5 py-0.5 rounded bg-[#6B8F71]/10 text-[#6B8F71]">
                                  {rule.action_payload?.action?.toUpperCase()}
                                  {rule.action_payload?.duration_minutes ? ` ${rule.action_payload.duration_minutes}m` : ''}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Active Schedules */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-[#1B263B]/40 dark:text-white/30" />
                  <h3 className="text-sm font-bold text-[#1B263B]/60 dark:text-white/50 uppercase tracking-wide">
                    {tc.activeSchedules}
                  </h3>
                  <span className="text-[0.6rem] font-bold px-2 py-0.5 rounded-full bg-[#1B263B]/5 dark:bg-white/5 text-[#1B263B]/40 dark:text-white/30">
                    {schedules.length}
                  </span>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  onClick={() => { setEditingSchedule(null); setIsBuilderOpen(true); }}
                  className="px-3 py-1.5 rounded-lg bg-[#C47A2C]/10 hover:bg-[#C47A2C]/20 text-[#C47A2C] text-xs font-bold flex items-center gap-1 transition-colors"
                >
                  <Plus size={14} /> {tc.newRule || '+ New Rule'}
                </motion.button>
              </div>

              {schedules.length === 0 ? (
                <Card className="!p-8 text-center">
                  <CalendarClock size={36} className="mx-auto text-[#1B263B]/10 dark:text-white/10 mb-3" />
                  <p className="text-xs text-[#1B263B]/40 dark:text-white/30">{tc.noSchedules}</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {schedules.map((s, i) => (
                    <ScheduleItem
                      key={s.id}
                      schedule={s}
                      onToggle={handleToggleSchedule}
                      onDelete={handleDeleteSchedule}
                      onEdit={openEditModal}
                      t={t}
                      index={i}
                    />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ScheduleBuilderModal 
        isOpen={isBuilderOpen}
        onClose={() => { setIsBuilderOpen(false); setEditingSchedule(null); }}
        onSave={handleSaveSchedule}
        actuators={actuators}
        t={t}
        editingSchedule={editingSchedule}
        userId={userId}
      />
    </div>
  );
};

export default ControlAutomationView;
